import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import { CreatePackPurchasePaymentRequest } from "../contracts/CreatePackPurchasePaymentRequest";
import { CreatePaymentVisitLinkRequest } from "../contracts/CreatePaymentVisitLinkRequest";
import { CreateStandalonePaymentRequest } from "../contracts/CreateStandalonePaymentRequest";
import { MarkPaymentStatusRequest } from "../contracts/MarkPaymentStatusRequest";
import { PaginatedPaymentResult } from "../contracts/PaginatedPaymentResult";
import { PaymentBucketResponse } from "../contracts/PaymentBucketResponse";
import { PaymentReminderResponse } from "../contracts/PaymentReminderResponse";
import { PaymentResponse } from "../contracts/PaymentResponse";
import { PaymentStatusChangeResponse } from "../contracts/PaymentStatusChangeResponse";
import { PaymentVisitLinkResponse } from "../contracts/PaymentVisitLinkResponse";
import { VoidPaymentRequest } from "../contracts/VoidPaymentRequest";
import {
  mapResponseToBucket,
  mapResponseToPayment,
  mapResponseToStatusChange,
  mapResponseToVisitLink,
} from "../mappers/PaymentMapper";
import { Payment } from "../models/Payment";
import { PaymentBucket } from "../models/PaymentBucket";
import { PaymentStatus } from "../models/PaymentStatus";
import { PaymentStatusChange } from "../models/PaymentStatusChange";
import { PaymentVisitLink } from "../models/PaymentVisitLink";

const BASE = "/payments";

export interface FetchPaymentsArgs {
  memberId?: string;
  contractId?: string;
  status?: PaymentStatus;
  page?: number;
  pageSize?: number;
}

export interface PaginatedPayments {
  items: Payment[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export const fetchPayments = createAsyncThunk<
  PaginatedPayments,
  FetchPaymentsArgs | void,
  { rejectValue: ApiErrorResponse }
>("payments/fetchAll", async (args, { rejectWithValue }) => {
  try {
    const params: Record<string, string | number> = {
      page: (args && args.page) ?? 1,
      pageSize: (args && args.pageSize) ?? 20,
    };
    if (args?.memberId) params.memberId = args.memberId;
    if (args?.contractId) params.contractId = args.contractId;
    if (args?.status) params.status = args.status;

    const response = await axiosInstance.get<PaginatedPaymentResult>(BASE, { params });
    return {
      items: response.data.items.map(mapResponseToPayment),
      page: response.data.page,
      pageSize: response.data.pageSize,
      totalCount: response.data.totalCount,
      totalPages: response.data.totalPages,
      hasPreviousPage: response.data.hasPreviousPage,
      hasNextPage: response.data.hasNextPage,
    };
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load payments"));
  }
});

export const createPackPurchasePayment = createAsyncThunk<
  Payment,
  CreatePackPurchasePaymentRequest,
  { rejectValue: ApiErrorResponse }
>("payments/createPackPurchase", async (request, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<PaymentResponse>(`${BASE}/pack-purchase`, request);
    return mapResponseToPayment(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to record pack-purchase payment"));
  }
});

export const createStandalonePayment = createAsyncThunk<
  Payment,
  CreateStandalonePaymentRequest,
  { rejectValue: ApiErrorResponse }
>("payments/createStandalone", async (request, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<PaymentResponse>(`${BASE}/standalone`, request);
    return mapResponseToPayment(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to record standalone payment"));
  }
});

export const confirmPayment = createAsyncThunk<
  Payment,
  { id: string; request: MarkPaymentStatusRequest },
  { rejectValue: ApiErrorResponse }
>("payments/confirm", async ({ id, request }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<PaymentResponse>(`${BASE}/${id}/confirm`, request);
    return mapResponseToPayment(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to confirm payment"));
  }
});

export const markPaymentReceived = createAsyncThunk<
  Payment,
  { id: string; request: MarkPaymentStatusRequest },
  { rejectValue: ApiErrorResponse }
>("payments/markReceived", async ({ id, request }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<PaymentResponse>(`${BASE}/${id}/mark-received`, request);
    return mapResponseToPayment(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to mark payment received"));
  }
});

export const markPaymentOverdue = createAsyncThunk<
  Payment,
  { id: string; request: MarkPaymentStatusRequest },
  { rejectValue: ApiErrorResponse }
>("payments/markOverdue", async ({ id, request }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<PaymentResponse>(`${BASE}/${id}/mark-overdue`, request);
    return mapResponseToPayment(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to mark payment overdue"));
  }
});

export const voidPayment = createAsyncThunk<
  Payment,
  { id: string; request: VoidPaymentRequest },
  { rejectValue: ApiErrorResponse }
>("payments/void", async ({ id, request }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<PaymentResponse>(`${BASE}/${id}/void`, request);
    return mapResponseToPayment(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to void payment"));
  }
});

export const updatePaymentDocumentNote = createAsyncThunk<
  Payment,
  { id: string; documentNote: string | null },
  { rejectValue: ApiErrorResponse }
>("payments/updateDocumentNote", async ({ id, documentNote }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put<PaymentResponse>(`${BASE}/${id}/document-note`, {
      documentNote,
    });
    return mapResponseToPayment(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to save document note"));
  }
});

export const downloadPaymentDocument = createAsyncThunk<
  Payment,
  { id: string },
  { rejectValue: ApiErrorResponse }
>("payments/downloadDocument", async ({ id }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<Blob>(`${BASE}/${id}/document`, null, {
      responseType: "blob",
    });

    const disposition = response.headers["content-disposition"] as string | undefined;
    const fileName = disposition?.match(/filename="?([^";]+)"?/)?.[1] ?? `document-${id}.pdf`;

    const url = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // First generation assigns the sequential invoice number — refresh the row so it shows.
    const refreshed = await axiosInstance.get<PaymentResponse>(`${BASE}/${id}`);
    return mapResponseToPayment(refreshed.data);
  } catch (error) {
    return rejectWithValue(await toDocumentApiError(error));
  }
});

// Blob-typed requests wrap error JSON in a Blob — unwrap it so the UI can show
// the server's message (e.g. "business details not configured").
const toDocumentApiError = async (error: unknown): Promise<ApiErrorResponse> => {
  const maybeAxios = error as { response?: { data?: unknown } };
  const data = maybeAxios.response?.data;
  if (data instanceof Blob && data.type.includes("json")) {
    try {
      maybeAxios.response!.data = JSON.parse(await data.text());
    } catch {
      // fall through to the generic mapping
    }
  }
  return toApiError(error, "Failed to generate the document");
};

export const fetchPaymentStatusHistory = createAsyncThunk<
  { paymentId: string; changes: PaymentStatusChange[] },
  string,
  { rejectValue: ApiErrorResponse }
>("payments/fetchStatusHistory", async (paymentId, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<PaymentStatusChangeResponse[]>(
      `${BASE}/${paymentId}/status-history`);
    return { paymentId, changes: response.data.map(mapResponseToStatusChange) };
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load payment status history"));
  }
});

export const linkVisitToPayment = createAsyncThunk<
  { paymentId: string; link: PaymentVisitLink },
  { paymentId: string; request: CreatePaymentVisitLinkRequest },
  { rejectValue: ApiErrorResponse }
>("payments/linkVisit", async ({ paymentId, request }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<PaymentVisitLinkResponse>(
      `${BASE}/${paymentId}/visit-links`, request);
    return { paymentId, link: mapResponseToVisitLink(response.data) };
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to settle visit against payment"));
  }
});

export const fetchPaymentVisitLinks = createAsyncThunk<
  { paymentId: string; links: PaymentVisitLink[] },
  string,
  { rejectValue: ApiErrorResponse }
>("payments/fetchVisitLinks", async (paymentId, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<PaymentVisitLinkResponse[]>(
      `${BASE}/${paymentId}/visit-links`);
    return { paymentId, links: response.data.map(mapResponseToVisitLink) };
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load payment visit links"));
  }
});

export const unlinkVisitFromPayment = createAsyncThunk<
  { paymentId: string; linkId: string },
  { paymentId: string; linkId: string },
  { rejectValue: ApiErrorResponse }
>("payments/unlinkVisit", async ({ paymentId, linkId }, { rejectWithValue }) => {
  try {
    await axiosInstance.delete(`${BASE}/${paymentId}/visit-links/${linkId}`);
    return { paymentId, linkId };
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to unlink visit"));
  }
});

export const fetchPaymentBuckets = createAsyncThunk<
  { paymentId: string; buckets: PaymentBucket[] },
  string,
  { rejectValue: ApiErrorResponse }
>("payments/fetchBuckets", async (paymentId, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<PaymentBucketResponse[]>(
      `${BASE}/${paymentId}/buckets`);
    return { paymentId, buckets: response.data.map(mapResponseToBucket) };
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load payment buckets"));
  }
});

/**
 * Emails the member that an overdue Payment is still open. Instructor-triggered —
 * nothing goes out on a schedule.
 */
export const sendPaymentReminder = createAsyncThunk<
  PaymentReminderResponse,
  string,
  { rejectValue: ApiErrorResponse }
>("payments/sendReminder", async (paymentId, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<PaymentReminderResponse>(
      `/payments/${paymentId}/reminder`,
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});
